import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { errorMessage } from '../lib/api';
import { shortDate } from '../lib/format';
import { fetchMe } from '../queries/account';
import { Banner, Button, ErrorText } from './ui';

/**
 * "Confirm your email address", on every page until they do.
 *
 * A password signup proves nothing about the address typed in, so the
 * account runs on trust for a week and then recording waits for the
 * confirmation. The banner says which it is: a nudge with the date while
 * the week runs, a stop once it has. Google accounts arrive confirmed and
 * never see it. The API's word is used rather than Firebase's, because the
 * API is what will refuse the recording.
 */
export function VerifyEmailBanner() {
  const { user, sendVerification } = useAuth();
  const me = useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled: Boolean(user), staleTime: 60_000 });
  const send = useMutation({ mutationFn: sendVerification });

  if (!me.data || me.data.emailVerified !== false) return null;
  const deadline = me.data.verifyBy ? new Date(me.data.verifyBy) : null;
  const overdue = deadline !== null && deadline.getTime() <= Date.now();

  return (
    <div className="mb-6">
      <Banner
        tone={overdue ? 'danger' : 'caution'}
        title={overdue ? 'Recording is paused until you confirm your email address' : 'Confirm your email address'}
        action={
          <div className="flex items-center gap-3">
            {send.isSuccess ? <span className="text-caption text-go">Sent. Check your inbox.</span> : null}
            <Button size="sm" variant={overdue ? 'primary' : undefined} busy={send.isPending} disabled={send.isSuccess} onClick={() => send.mutate()}>
              {send.isSuccess ? 'Sent' : 'Send the confirmation email'}
            </Button>
          </div>
        }
      >
        {overdue
          ? `We sent a confirmation link to ${me.data.email}. Open it and everything carries on where it was.`
          : `We need to know ${me.data.email} is yours${deadline ? ` by ${shortDate(deadline.toISOString())}` : ''}; after that, recording waits for it. Everything else keeps working.`}
        {send.isError ? (
          <div className="mt-1">
            <ErrorText>{errorMessage(send.error)}</ErrorText>
          </div>
        ) : null}
      </Banner>
    </div>
  );
}
